import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { Conversation, Message, TextOrFileMessage } from '../../types';
import { groupMessagesByDate } from '../../utils';
import { ChatDateSeparator } from '../messages/ChatDateSeparator';
import { ChatMessageBubble } from '../messages/ChatMessageBubble';

/**
 * The conversation messages in a chat box.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatBox}
 */
export function ChatBoxMessages({
    className,
    conversation,
    messages,
    scrollDownCounter = 0,
    isOpen,
    isLoadingMore = false,
    hasMore = false,
    hideActions,
    onLoadMore,
    onReplyMessage,
    onForwardMessage,
    onDeleteMessage,
}: {
    className?: string;
    conversation?: Conversation;
    messages: Message[];
    /**
     * Update this variable to trigger scroll down to the bottom, except when the
     * user has manually scrolled up.
     */
    scrollDownCounter?: number;
    isOpen: boolean;
    /**
     * The next page of messages is loading.
     */
    isLoadingMore?: boolean;
    /**
     * There are more messages to load.
     */
    hasMore?: boolean;
    /**
     * Hide the actions for all message bubbles. Defaults to `false`.
     */
    hideActions?: boolean;
    /**
     * Load more messages by fetching the next page. Contains a callback to be
     * called when the next page is loaded, this will scroll the container down
     * to keep the same distance to the bottom of the container.
     */
    onLoadMore?: () => void;
    /**
     * Callback when the reply button is clicked on a message.
     */
    onReplyMessage?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the forward button is clicked on a message.
     */
    onForwardMessage?: (message: TextOrFileMessage) => void;
    /**
     * Callback when the delete button is clicked on a message.
     */
    onDeleteMessage?: (messageID: number) => void;
}) {
    const [isUserScrolled, setIsUserScrolled] = useState(false);
    const [loadMoreBottomDistance, setLoadMoreBottomDistance] = useState<
        number | null
    >(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const groupedMessages = useMemo(() => {
        return groupMessagesByDate(messages);
    }, [messages]);

    // Scroll to the bottom when the chat box is opened, the messages are
    // updated, or other events such as toggling reply mode (except when the
    // user has manually scrolled up).
    useEffect(() => {
        if (!isOpen) return;
        // Scroll to the bottom when previously scrolled to the bottom or when
        // the user sends a new message.
        if (!isUserScrolled || messages[0]?.isOptimistic) {
            containerRef.current?.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: 'instant',
            });
        }
        // Do not pass `isUserScrolled` on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, messages, scrollDownCounter]);

    // Trigger load more messages when the user scrolls to the top.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = containerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoadingMore) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    onLoadMore &&
                    entry?.isIntersecting &&
                    hasMore &&
                    !isLoadingMore
                ) {
                    const distanceToBottom =
                        scrollContainer.scrollHeight -
                        scrollContainer.scrollTop -
                        scrollContainer.clientHeight;
                    onLoadMore();
                    // Scroll down when the next page finishes loading.
                    setLoadMoreBottomDistance(distanceToBottom);
                }
            },
            { root: scrollContainer }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoadingMore, onLoadMore]);

    // Scroll down to keep bottom distance after next page of messages is loaded.
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new MutationObserver(() => {
            if (loadMoreBottomDistance !== null && !isLoadingMore) {
                if (isUserScrolled) {
                    containerRef.current?.scrollTo({
                        top:
                            containerRef.current.scrollHeight -
                            containerRef.current.clientHeight -
                            loadMoreBottomDistance,
                        behavior: 'instant',
                    });
                }
                setLoadMoreBottomDistance(null);
            }
        });
        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [loadMoreBottomDistance, isLoadingMore, isUserScrolled]);

    return (
        <ScrollArea
            ref={containerRef}
            className={cn(
                // Fix for width overflow issue.
                // https://github.com/radix-ui/primitives/issues/2722
                '[&>div>div]:!block',
                className
            )}
            scrollBarClassName="w-[8px]"
            thumbClassName="bg-gray-500"
            overscroll="contain"
            onCustomScroll={(val) => {
                setIsUserScrolled(val.bottom >= 100);
                if (loadMoreBottomDistance !== null) {
                    setLoadMoreBottomDistance(val.bottom);
                }
            }}
        >
            <div
                className="relative flex select-text flex-col gap-[8px] p-[12px]"
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
            >
                {hasMore && (
                    <div
                        ref={loadMoreSentinelRef}
                        className="invisible absolute inset-x-0 top-[400px] h-px"
                        aria-hidden="true"
                    ></div>
                )}
                {groupedMessages.map(({ date, a11yString, messages }) => (
                    <React.Fragment key={a11yString}>
                        <ChatDateSeparator
                            className="mx-auto my-[8px] min-w-[25%]"
                            date={date}
                        />
                        {messages.map((message, index, messages) => {
                            const previousMessage =
                                index > 0 ? messages[index - 1] : undefined;
                            const previousSender = previousMessage?.Participant;
                            return (
                                <ChatMessageBubble
                                    key={message.ID}
                                    message={message}
                                    conversation={conversation}
                                    professional={
                                        conversation?.__typename ===
                                        'EnquiryConversation'
                                            ? conversation.InitialEnquiry
                                                  .Professional
                                            : undefined
                                    }
                                    hideSender={
                                        previousMessage &&
                                        previousSender &&
                                        message.Participant.ID ===
                                            previousSender.ID &&
                                        previousMessage.__typename !==
                                            'SystemMessage' &&
                                        previousMessage.__typename !==
                                            'RevealContactDetailsMessage'
                                    }
                                    hideActions={hideActions}
                                    onReply={onReplyMessage}
                                    onForward={onForwardMessage}
                                    onDelete={() => {
                                        onDeleteMessage?.(Number(message.ID));
                                    }}
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </ScrollArea>
    );
}
